import { useCallback, useEffect, useMemo, useState } from "react";
import type { DashboardFinanceiroFiltros } from "../types/financeiro-dashboard.types";
import type {
  DreGerencialApiResponse,
  DREGerencialView,
} from "../types/dre-gerencial.types";
import { mapDreApiToView } from "../helpers/map-dre-api-to-view";
import { obterDREGerencial } from "../services/dre-gerencial.service";

export type UseDREGerencialResult = {
  rawData: DreGerencialApiResponse | null;
  data: DREGerencialView | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  isEmpty: boolean;
};

export function useDREGerencial(
  filtrosAplicados: DashboardFinanceiroFiltros,
): UseDREGerencialResult {
  const [rawData, setRawData] = useState<DreGerencialApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const paramsQuery = useMemo(
    () => ({
      dataInicio: filtrosAplicados.dataInicio,
      dataFim: filtrosAplicados.dataFim,
      granularidade: filtrosAplicados.granularidade,
    }),
    [filtrosAplicados],
  );

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, status } = await obterDREGerencial(paramsQuery);
        if (cancelled) return;
        if (status === 403) {
          setError("Sem permissão para o DRE gerencial.");
          setRawData(null);
          return;
        }
        if (status !== 200 || !data) {
          setError("Não foi possível carregar o DRE gerencial.");
          setRawData(null);
          return;
        }
        setRawData(data);
      } catch (e: unknown) {
        if (cancelled) return;
        const msg =
          e instanceof Error ? e.message : "Falha ao carregar o DRE gerencial.";
        setError(msg);
        setRawData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [paramsQuery, tick]);

  const data = useMemo(
    () => (rawData ? mapDreApiToView(rawData) : null),
    [rawData],
  );

  const isEmpty = useMemo(() => {
    if (!data) return false;
    const temMovimento =
      data.receitaBrutaTotal !== 0 ||
      data.receitaLiquida !== 0 ||
      data.deducoesTotal !== 0 ||
      data.taxasGateway !== 0 ||
      data.estornos !== 0 ||
      data.cmvTotal !== 0 ||
      data.despesasTotal !== 0 ||
      data.lucroOperacional !== 0;
    return !temMovimento;
  }, [data]);

  const refetch = useCallback(() => {
    setTick((n) => n + 1);
  }, []);

  return {
    rawData,
    data,
    loading,
    error,
    refetch,
    isEmpty,
  };
}
